import { createSlice } from "@reduxjs/toolkit";

const AI_USER = {
  id: "ai-assistant",
  name: "AI Enterprise Assistant",
  role: "AI Automation & Workflows",
  isAi: true,
};

const initialState = {
  conversations: [],
  conversationsLoading: false,
  selectedUser: AI_USER,
  activeConversation: null,
  messagesMap: {}, // Keyed by conversationId
  messagesLoading: false,
  sendMessageLoading: false,
  error: null,
};

const chatSlice = createSlice({
  name: "companyChat",
  initialState,
  reducers: {
    // Select Chat Target User
    setSelectedUser: (state, action) => {
      state.selectedUser = action.payload;
    },

    // Set Active Conversation
    setActiveConversation: (state, action) => {
      state.activeConversation = action.payload;
    },

    // Fetch Conversations
    fetchConversationsStart: (state) => {
      state.conversationsLoading = true;
      state.error = null;
    },
    fetchConversationsSuccess: (state, action) => {
      state.conversationsLoading = false;
      state.conversations = action.payload;
      state.error = null;
    },
    fetchConversationsFailure: (state, action) => {
      state.conversationsLoading = false;
      state.error = action.payload;
    },

    // Fetch Messages
    fetchMessagesStart: (state) => {
      state.messagesLoading = true;
      state.error = null;
    },
    fetchMessagesSuccess: (state, action) => {
      const { conversationId, messages } = action.payload;
      state.messagesLoading = false;
      state.messagesMap[conversationId] = messages;
      state.error = null;
    },
    fetchMessagesFailure: (state, action) => {
      state.messagesLoading = false;
      state.error = action.payload;
    },

    // Send Message
    sendMessageStart: (state) => {
      state.sendMessageLoading = true;
      state.error = null;
    },
    sendMessageSuccess: (state, action) => {
      const { conversationId, message } = action.payload;
      state.sendMessageLoading = false;

      const existingMsgs = state.messagesMap[conversationId] || [];
      state.messagesMap[conversationId] = [...existingMsgs, message];
      state.error = null;
    },
    sendMessageFailure: (state, action) => {
      state.sendMessageLoading = false;
      state.error = action.payload;
    },

    // Mark Read
    markReadSuccess: (state, action) => {
      const conversationId = action.payload;
      const conversation = state.conversations.find((c) => c._id === conversationId);
      if (conversation && conversation.unreadCounts) {
        conversation.unreadCounts = {};
      }
    },

    // Receive Real-time SSE Chat Message
    receiveSseMessage: (state, action) => {
      const { conversationId, message, lastMessage, unreadCounts } = action.payload || {};
      if (!conversationId || !message) return;

      // 1. Append message to messagesMap if not already present
      const existingMsgs = state.messagesMap[conversationId] || [];
      const msgId = message._id || message.id;
      const isAlreadyPresent = existingMsgs.some(
        (m) => (m._id || m.id) === msgId
      );

      if (!isAlreadyPresent) {
        state.messagesMap[conversationId] = [...existingMsgs, message];
      }

      // 2. Update conversation list preview & unread counts
      const convIndex = state.conversations.findIndex((c) => c._id === conversationId);
      if (convIndex !== -1) {
        if (lastMessage) {
          state.conversations[convIndex].lastMessage = lastMessage;
        }
        if (unreadCounts) {
          state.conversations[convIndex].unreadCounts = unreadCounts;
        }
      }
    },

    // Clear Error
    clearChatError: (state) => {
      state.error = null;
    },
  },
});

export const {
  setSelectedUser,
  setActiveConversation,
  fetchConversationsStart,
  fetchConversationsSuccess,
  fetchConversationsFailure,
  fetchMessagesStart,
  fetchMessagesSuccess,
  fetchMessagesFailure,
  sendMessageStart,
  sendMessageSuccess,
  sendMessageFailure,
  markReadSuccess,
  receiveSseMessage,
  clearChatError,
} = chatSlice.actions;

export default chatSlice.reducer;
